import { Employee, TeamData, BudgetData } from '../contexts/AppContext';

// AssistCashierDoctorData 타입 정의
export interface AssistCashierDoctorDataItem {
  assistName: string;
  cashierName?: string;
  doctorName?: string;
}

export type AssistCashierDoctorData = Record<string, {
  assistName: string;
  cashierName: string;
  matchedDoctor: string;
}>;

/**
 * 복합 팀 데이터 소스 생성 유틸리티
 * 급여 관리와 팀 성과 관리에서 공통으로 사용
 */
export class TeamDataUtils {
  
  /**
   * 복합 팀 데이터 소스를 생성합니다
   * @param teams 기본 팀 데이터
   * @param employees 직원 데이터  
   * @param budgetData 예산 데이터
   * @param yearMonth 년월 (옵션, 월별 데이터 포함 시 사용)
   * @returns 정렬된 유효한 팀 이름 배열
   */
  static generateCompositeTeamData(
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData,
    yearMonth?: string
  ): string[] {
    // 🔧 FIX: 직원 기반 필터링 - ghost 팀 제거 (team-budget-sync-fix)
    // 기존: Source 1 ∪ Source 2 ∪ Source 3 (union → ghost 팀 포함)
    // 변경: employees 배열에 실제 배정된 팀만 반환

    // Step 1: 직원 데이터에서 실제 배정된 팀명 추출 (기준 데이터)
    const employeeTeamSet = new Set<string>();
    employees.forEach(emp => {
      if (emp?.team && emp.team.trim() !== '' && emp.team !== '미지정') {
        employeeTeamSet.add(emp.team.trim());
      }
    });

    // Step 2: 직원이 배정된 팀만 최종 목록에 포함
    // "미지정"은 ensureUnassignedTeamExists()에서 별도 추가됨
    const validTeamNames = Array.from(employeeTeamSet)
      .filter(teamName => teamName && teamName.trim() !== '')
      .sort();

    console.log(`[TeamDataUtils] 복합 팀 데이터 생성 (직원 기반 필터): ${validTeamNames.length}개 팀`, validTeamNames);

    return validTeamNames;
  }

  /**
   * 팀별 목표매출을 계산합니다
   * @param teamNames 팀 이름 배열
   * @param employees 직원 데이터
   * @param budgetData 예산 데이터
   * @param yearMonth 년월 (월별 저장 데이터 우선 사용)
   * @returns 팀별 목표매출 객체
   */
  static calculateTeamTargetSales(
    teamNames: string[],
    employees: Employee[],
    budgetData: BudgetData,
    yearMonth?: string
  ): Record<string, number> {
    const teamTargetSales: Record<string, number> = {};

    // 월별 저장된 목표매출 데이터 우선 확인
    const monthlyTeamTargetSales = yearMonth
      ? (budgetData.monthlyData?.[yearMonth]?.teamTargetSales || {})
      : {};

    teamNames.forEach(teamName => {
      // 1순위: 월별 저장 데이터
      if (monthlyTeamTargetSales[teamName] !== undefined) {
        teamTargetSales[teamName] = monthlyTeamTargetSales[teamName];
        console.log(`[TeamDataUtils] ${teamName} 월별 저장 목표매출: ${teamTargetSales[teamName].toLocaleString()}원`);
      }
      // 2순위: 전역 팀 데이터
      else if (budgetData.teamTargetSales?.[teamName] !== undefined) {
        teamTargetSales[teamName] = budgetData.teamTargetSales[teamName];
        console.log(`[TeamDataUtils] ${teamName} 전역 목표매출: ${teamTargetSales[teamName].toLocaleString()}원`);
      }
      // 3순위: 인건비율 기반 계산
      else if (budgetData.personnelPercentage > 0) {
        const teamEmployees = employees.filter(emp => emp.team === teamName);
        const teamSalaryTotal = this.calculateTeamSalaryTotal(teamEmployees);

        teamTargetSales[teamName] = Math.round(teamSalaryTotal / (budgetData.personnelPercentage / 100));
        console.log(`[TeamDataUtils] ${teamName} 계산 목표매출: ${teamTargetSales[teamName].toLocaleString()}원`);
      }
      // 기본값: 0
      else {
        teamTargetSales[teamName] = 0;
        console.log(`[TeamDataUtils] ${teamName} 기본 목표매출: 0원`);
      }
    });

    return teamTargetSales;
  }

  /**
   * 팀의 총 급여를 계산합니다
   * @param teamEmployees 팀 직원 배열
   * @returns 팀 총 급여
   */
  static calculateTeamSalaryTotal(teamEmployees: Employee[]): number {
    return teamEmployees.reduce((sum, emp) => 
      sum + (emp.salary || 0) + 
      (emp.retirement || 0) + 
      (emp.meal || 0) + 
      (emp.housing || 0) + 
      (emp.overtime || 0) + 
      (emp.individual || 0) + 
      (emp.other || 0), 0
    );
  }

  /**
   * 팀별 직원 수를 계산합니다
   * @param teamNames 팀 이름 배열
   * @param employees 직원 데이터
   * @returns 팀별 직원 수 객체
   */
  static calculateTeamMemberCounts(teamNames: string[], employees: Employee[]): Record<string, number> {
    const teamMemberCounts: Record<string, number> = {};
    
    teamNames.forEach(teamName => {
      teamMemberCounts[teamName] = employees.filter(emp => emp.team === teamName).length;
    });
    
    return teamMemberCounts;
  }

  /**
   * 팀별 급여 총액을 계산합니다
   * @param teamNames 팀 이름 배열
   * @param employees 직원 데이터
   * @returns 팀별 급여 총액 객체
   */
  static calculateTeamSalaryTotals(teamNames: string[], employees: Employee[]): Record<string, number> {
    const teamSalaryTotals: Record<string, number> = {};
    
    teamNames.forEach(teamName => {
      const teamEmployees = employees.filter(emp => emp.team === teamName);
      teamSalaryTotals[teamName] = this.calculateTeamSalaryTotal(teamEmployees);
    });
    
    return teamSalaryTotals;
  }

  /**
   * 팀 이름을 정규화합니다
   * @param teamName 원본 팀 이름
   * @returns 정규화된 팀 이름
   */
  static normalizeTeamName(teamName: string): string {
    return teamName ? teamName.trim() : '';
  }

  /**
   * 팀 배열에서 중복을 제거합니다
   * @param teamNames 팀 이름 배열
   * @returns 중복 제거된 팀 이름 배열
   */
  static deduplicateTeamNames(teamNames: string[]): string[] {
    const uniqueTeams = new Set(
      teamNames
        .map(name => this.normalizeTeamName(name))
        .filter(name => name && name !== '')
    );
    
    return Array.from(uniqueTeams).sort();
  }
}



/**
 * 통일된 assistCashierDoctorData 변환 함수
 * @param data 변환할 데이터
 * @returns 변환된 assistCashierDoctorData 객체
 */
export const transformAssistCashierDoctorData = (
  data: any[] | null | undefined
): AssistCashierDoctorData => {
  if (!data || data.length === 0) return {};
  
  return data.reduce((acc, item) => {
    const key = `${item.assistName}_${item.cashierName || ''}`;
    acc[key] = {
      assistName: item.assistName,
      cashierName: item.cashierName || '',
      matchedDoctor: item.doctorName || ''
    };
    return acc;
  }, {} as AssistCashierDoctorData);
}; 